import React, { useState } from 'react';
import { useContent } from '../i18n/LanguageContext';
import LiveMap from './LiveMap';
import { MapPin, BellRing, BarChart3, Radio } from 'lucide-react';

const TONES = {
  teal: 'text-teal-700 bg-teal-50',
  amber: 'text-amber-700 bg-amber-50',
  slate: 'text-slate-600 bg-slate-100',
  red: 'text-red-700 bg-red-50',
};
const DOTS = { teal: 'bg-teal-500', amber: 'bg-amber-500', slate: 'bg-slate-400', red: 'bg-red-500' };

export default function LiveMapSection() {
  const c = useContent();
  const s = c.liveSection;
  const lm = c.liveMap;

  const bullets = [
    { icon: MapPin, text: c.connectivity.items[3].title },
    { icon: BellRing, text: c.problems.items[3].hint },
    { icon: BarChart3, text: c.solutions.reports[1] },
  ];

  const [events, setEvents] = useState(() =>
    lm.assets.slice(0, 3).map((n, i) => ({ id: 'seed' + i, text: `${n} — ${lm.stable}`, tone: 'teal' }))
  );

  const pushEvent = (e) => {
    setEvents((prev) => [{ ...e, id: `${Date.now()}-${Math.random()}` }, ...prev].slice(0, 6));
  };

  return (
    <section id="live" className="pt-8 md:pt-10 pb-10 md:pb-12 bg-white">
      <div className="lt-container grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div className="reveal">
          <p className="text-sm font-semibold uppercase tracking-widest text-teal-600">{s.eyebrow}</p>
          <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold text-slate-900 leading-tight">{s.title}</h2>
          <p className="mt-4 text-lg text-slate-600 max-w-md">{s.subtitle}</p>

          <div className="mt-7 flex flex-wrap gap-3">
            {bullets.map((b) => (
              <div key={b.text} className="inline-flex items-center gap-2.5 rounded-full border border-slate-200 bg-white pl-1.5 pr-4 py-1.5 shadow-sm">
                <span className="grid place-items-center w-8 h-8 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 text-white">
                  <b.icon className="w-4 h-4" />
                </span>
                <span className="text-sm font-medium text-slate-700">{b.text}</span>
              </div>
            ))}
          </div>

          {/* Live activity feed */}
          <div className="mt-7 rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100 bg-slate-50">
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-800">
                <Radio className="w-4 h-4 text-teal-600" /> {lm.feedTitle}
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-teal-700">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-teal-500 opacity-75 animate-ping" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-teal-600" />
                </span>
                {lm.assets.length} {lm.online}
              </span>
            </div>
            <ul className="divide-y divide-slate-100 min-h-[190px]">
              {events.map((e) => (
                <li key={e.id} className="flex items-center gap-3 px-5 py-2.5 animate-[fadeIn_.4s_ease]">
                  <span className={`h-2 w-2 rounded-full shrink-0 ${DOTS[e.tone] || DOTS.teal}`} />
                  <span className="flex-1 text-sm font-medium text-slate-700">{e.text}</span>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${TONES[e.tone] || TONES.teal}`}>live</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="reveal">
          <div className="relative mx-auto max-w-md">
            <div className="absolute -inset-4 rounded-[2.5rem] bg-gradient-to-br from-teal-100 to-cyan-100 opacity-60 blur-lg" />
            <div className="relative"><LiveMap onEvent={pushEvent} /></div>
          </div>
        </div>
      </div>
    </section>
  );
}
